import {
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { createIdMap } from "@tellescope/utils";
import { useCallback, useMemo } from "react";

export interface UseInboxScheduledArgs {
  enabled?: boolean;
}

export type InboxScheduledItem =
  | (TellescopeModelFromName<"emails"> & { channel: "Email" })
  | (TellescopeModelFromName<"sms_messages"> & { channel: "SMS" })
  | (TellescopeModelFromName<"chats"> & { channel: "Chat" });

export const useInboxScheduledMessages = ({ enabled = true }: UseInboxScheduledArgs = {}) => {
  const smsMessagesQuery = useTellescopeInfiniteQuery({
    model: "sms_messages",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
        },
        delivered: false,
        isDraft: { $ne: true },
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"sms_messages"> & { channel: "SMS" }>((item) => ({ ...item, channel: "SMS" })),
    enabled,
  });
  const emailsQuery = useTellescopeInfiniteQuery({
    model: "emails",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
        },
        delivered: false,
        isDraft: { $ne: true },
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"emails"> & { channel: "Email" }>((item) => ({ ...item, channel: "Email" })),
    enabled,
  });
  const chatsQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      limit: 1000,
      mdbFilter: {
        sendAt: {
          $exists: true,
          $ne: "",
          $gt: new Date(1).toISOString(),
        },
        timestamp: {
          $exists: false,
        },
        isDraft: { $ne: true },
      },
    },
    select: (data) =>
      data.pages
        .flat()
        .map<TellescopeModelFromName<"chats"> & { channel: "Chat" }>((item) => ({ ...item, channel: "Chat" })),
    enabled,
  });

  const isLoadingMessages = smsMessagesQuery.isLoading || emailsQuery.isLoading || chatsQuery.isLoading;
  const messagesIsError = smsMessagesQuery.isError || emailsQuery.isError || chatsQuery.isError;

  const { messages, enduserIds, chatroomIds } = useMemo<{
    messages: InboxScheduledItem[];
    enduserIds: string[];
    chatroomIds: string[];
  }>(() => {
    if (isLoadingMessages) {
      // short circuit to not prematurely display a mis-ordered list of drafts when some are still loading
      return {
        messages: [],
        enduserIds: [],
        chatroomIds: [],
      };
    }

    const messages = [...(smsMessagesQuery.data ?? []), ...(emailsQuery.data ?? []), ...(chatsQuery.data ?? [])].sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
    );

    const { enduserIds, chatroomIds } = messages.reduce(
      (acc, message) => {
        if (message.enduserId) {
          acc.enduserIds.add(message.enduserId);
        }
        if (message.channel === "Chat") {
          acc.chatroomIds.add(message.roomId);
        }
        return acc;
      },
      { enduserIds: new Set<string>(), chatroomIds: new Set<string>() }
    );

    return {
      messages,
      enduserIds: Array.from(enduserIds),
      chatroomIds: Array.from(chatroomIds),
    };
  }, [chatsQuery.data, emailsQuery.data, isLoadingMessages, smsMessagesQuery.data]);

  const endusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: enduserIds,
    },
    enabled: !!enduserIds.length,
    select: (data) => ({
      endusers: data.matches,
      endusersMap: createIdMap(data.matches),
    }),
  });

  const getEndusers = useCallback(
    (ids?: string[] | string) => {
      const normalizedIds = ids === undefined ? [] : typeof ids === "string" ? [ids] : ids;
      return normalizedIds.reduce<TellescopeModelFromName<"endusers">[]>((acc, curr) => {
        const enduser = endusersQuery.data?.endusersMap[curr];
        if (enduser) {
          acc.push(enduser);
        }
        return acc;
      }, []);
    },
    [endusersQuery.data?.endusersMap]
  );

  const chatroomsQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getByIds",
    request: { ids: chatroomIds },
    select: (data) => ({
      chatrooms: data.matches,
      chatroomsMap: createIdMap(data.matches),
    }),
    enabled: !!chatroomIds.length,
  });

  return {
    messages,
    isLoadingMessages,
    messagesIsError,
    endusers: endusersQuery.data,
    isLoadingEndusers: endusersQuery.isLoading,
    endusersIsError: endusersQuery.isError,
    chatrooms: chatroomsQuery.data,
    chatroomsIsLoading: chatroomsQuery.isLoading,
    chatroomsIsError: chatroomsQuery.isError,
    isLoading: isLoadingMessages || endusersQuery.isLoading || chatroomsQuery.isLoading,
    isError: messagesIsError || endusersQuery.isError || chatroomsQuery.isError,
    getEndusers,
  };
};
